import imagesLoaded from 'imagesloaded';

// preload images
const preloadImages = (selector = 'img') =>
	new Promise(resolve => {
		imagesLoaded(document.querySelectorAll(selector), { background: true }, resolve);
	});

// Number.EPSILON
if (Number.EPSILON === undefined) {
	Number.EPSILON = 2 ** -52;
}

// slugify-ish normaliser for matching
const normalize = (value = '') =>
	value
		.toString()
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-');

// get parent using selector
const getClosest = (elem, selector) => {
	for (let element = elem; element && element !== document; element = element.parentNode) {
		if (element.matches(selector)) return element;
	}
	return null;
};

// get siblings
const getSiblings = elem => {
	// Setup siblings array and get the first sibling
	const siblings = [];
	let sibling = elem.parentNode.firstChild;

	// Loop through each sibling and push to the array
	while (sibling) {
		if (sibling.nodeType === 1 && sibling !== elem) {
			siblings.push(sibling);
		}
		sibling = sibling.nextSibling;
	}

	return siblings;
};

// handle promise response
const handleResponse = response =>
	response.json().then(json => {
		if (response.ok) return json;

		const error = Object.assign(...json, {
			status: response.status,
			statusText: response.statusText,
		});

		return Promise.reject(error);
	});

// clamp
const clamp = (val, min, max) => {
	const formatVal = Math.round((val + Number.EPSILON) * 100) / 100;
	if (formatVal > max) return max;
	if (formatVal < min) return min;
	return formatVal;
};

// debounce
const debounce = (func, timeout = 300) => {
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			func.apply(this, args);
		}, timeout);
	};
};

// throttle
const throttle = (func, limit) => {
	let lastFunc;
	let lastRan;
	return function invokeFn(...args) {
		const context = this;
		if (!lastRan) {
			func.apply(context, args);
			lastRan = Date.now();
		} else {
			clearTimeout(lastFunc);
			lastFunc = setTimeout(
				() => {
					if (Date.now() - lastRan >= limit) {
						func.apply(context, args);
						lastRan = Date.now();
					}
				},
				limit - (Date.now() - lastRan)
			);
		}
	};
};

// getBBox
const getBBox = (element, withoutTransforms, toElement) => {
	const svg = element.ownerSVGElement;
	if (!svg) {
		return {
			x: 0,
			y: 0,
			width: 0,
			height: 0,
		};
	}

	if (withoutTransforms) {
		return element.getBBox();
	}

	const p = svg.createSVGPoint();
	const r = element.getBBox();

	const matrix = (toElement || svg).getScreenCTM().inverse().multiply(element.getScreenCTM());

	p.x = r.x;
	p.y = r.y;
	const a = p.matrixTransform(matrix);

	p.x = r.x + r.width;
	p.y = r.y;
	const b = p.matrixTransform(matrix);

	p.x = r.x + r.width;
	p.y = r.y + r.height;
	const c = p.matrixTransform(matrix);

	p.x = r.x;
	p.y = r.y + r.height;
	const d = p.matrixTransform(matrix);

	const minX = Math.min(a.x, b.x, c.x, d.x);
	const maxX = Math.max(a.x, b.x, c.x, d.x);
	const minY = Math.min(a.y, b.y, c.y, d.y);
	const maxY = Math.max(a.y, b.y, c.y, d.y);

	const width = maxX - minX;
	const height = maxY - minY;

	return {
		x: minX,
		y: minY,
		width,
		height,
		cx: minX + width / 2,
		cy: minY + height / 2,
	};
};

// capitalise first letters
const capFirstLetter = string => {
	const words = string.split(' ');

	for (let i = 0; i < words.length; i += 1) {
		words[i] = words[i][0].toUpperCase() + words[i].substr(1);
	}

	return words.join(' ');
};

const loaderHTML = `
    <div class="flex items-center justify-center absolute top-0 left-0 z-50 w-full h-full bg-cream bg-opacity-75 js-map-loader">
        <svg
            class="w-24"
            viewBox="0 0 100 100">
            <path
                fill="#fb7d29"
                d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                <animateTransform
                    attributeName="transform"
                    attributeType="XML"
                    type="rotate"
                    dur="1s"
                    from="0 50 50"
                    to="360 50 50"
                    repeatCount="indefinite" />
            </path>
        </svg>
    </div>
`;

export {
	getClosest,
	getSiblings,
	handleResponse,
	clamp,
	debounce,
	throttle,
	getBBox,
	capFirstLetter,
	preloadImages,
	loaderHTML,
	normalize,
};
